import React, { useState } from 'react';
import { Box, Button, ListItemIcon, Menu, MenuItem, Popover, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';

const visibilityIcons = {
  private: LockOutlinedIcon,
  team: GroupsOutlinedIcon,
  organization: BusinessOutlinedIcon,
  company: BusinessOutlinedIcon,
  community: PublicOutlinedIcon,
  custom: TuneOutlinedIcon,
};

export default function BuilderOrganizationControls({
  visibility, visibilityOptions, visibilityDescriptions = {}, onVisibilityChange,
  onManagePermissions, projectName, projectSelector, onCreateProject, projectsDisabled = false,
  iconOnlyOnMobile = false,
}) {
  const [accessAnchor, setAccessAnchor] = useState(null);
  const [projectAnchor, setProjectAnchor] = useState(null);
  const label = (value) => value.charAt(0).toUpperCase() + value.slice(1);
  const buttonSx = {
    color: '#475569', textTransform: 'none', fontSize: '0.875rem', fontWeight: 500, letterSpacing: 0, height: { xs: iconOnlyOnMobile ? 32 : 36, sm: 36 }, maxWidth: '100%', px: 1,
    '&:hover': { color: 'primary.main', backgroundColor: 'action.hover' },
    ...(iconOnlyOnMobile && {
      minWidth: { xs: 32, sm: 'auto' },
      width: { xs: 32, sm: 'auto' },
      px: { xs: 0.5, sm: 1 },
      '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 }, ml: { xs: 0, sm: -0.5 } },
      '& .MuiButton-endIcon': { display: { xs: 'none', sm: 'inherit' } },
    }),
  };
  return (
    <>
      <Button size="small" startIcon={<SecurityOutlinedIcon />} endIcon={<ExpandMoreIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
        aria-label={`Permissions: ${label(visibility)}`} aria-haspopup="menu" aria-expanded={Boolean(accessAnchor)}
        onClick={(event) => setAccessAnchor(event.currentTarget)} sx={{
          ...buttonSx,
          ...(iconOnlyOnMobile && {
            minWidth: { xs: 44, sm: 'auto' },
            width: { xs: 44, sm: 'auto' },
            '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 }, ml: { xs: 0, sm: -0.5 } },
            '& .MuiButton-endIcon': { display: 'inherit', ml: { xs: 0, sm: 1 }, mr: { xs: 0, sm: -0.5 } },
          }),
        }}>
        <Box component="span" sx={iconOnlyOnMobile ? { display: { xs: 'none', sm: 'inline' } } : undefined}>{label(visibility)}</Box>
      </Button>
      <Menu anchorEl={accessAnchor} open={Boolean(accessAnchor)} onClose={() => setAccessAnchor(null)}>
        {visibilityOptions.map((option) => {
          const VisibilityIcon = visibilityIcons[option] || LockOutlinedIcon;
          return (
          <MenuItem key={option} selected={visibility === option} onClick={() => {
            if (option !== 'custom' || !onManagePermissions) setAccessAnchor(null);
            onVisibilityChange(option);
          }}>
            <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
            <Box sx={{ maxWidth: 280, whiteSpace: 'normal' }}>
              <Typography variant="body2" fontWeight={500}>{label(option)}</Typography>
              {visibilityDescriptions[option] && <Typography variant="caption" color="text.secondary">{visibilityDescriptions[option]}</Typography>}
            </Box>
          </MenuItem>
          );
        })}
        {onManagePermissions && (
          <MenuItem
            disabled={visibility !== 'custom'}
            onClick={() => { setAccessAnchor(null); onManagePermissions(); }}
          >
            <ListItemIcon><TuneOutlinedIcon fontSize="small" /></ListItemIcon>
            Manage custom permissions...
          </MenuItem>
        )}
      </Menu>
      {projectSelector && (
        <>
          <Button size="small" startIcon={<FolderOutlinedIcon />} endIcon={<ExpandMoreIcon />}
            disabled={projectsDisabled} aria-label={`Project: ${projectName || 'No project'}`}
            aria-haspopup="dialog" aria-expanded={Boolean(projectAnchor)}
            onClick={(event) => setProjectAnchor(event.currentTarget)} sx={{ ...buttonSx, maxWidth: 220 }}>
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...(iconOnlyOnMobile && { display: { xs: 'none', sm: 'inline' } }) }}>{projectName || 'No project'}</Box>
          </Button>
          <Popover anchorEl={projectAnchor} open={Boolean(projectAnchor)} onClose={() => setProjectAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <Box role="dialog" aria-label="Select project" sx={{ p: 2, width: 360, maxWidth: 'calc(100vw - 32px)', boxSizing: 'border-box' }}>
              {projectSelector}
              {onCreateProject && <Button size="small" onClick={() => { setProjectAnchor(null); onCreateProject(); }} sx={{ mt: 1 }}>Create project...</Button>}
            </Box>
          </Popover>
        </>
      )}
    </>
  );
}
